import { useMemo, useState, type ReactNode } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { PartyFormModal, type PartyKind } from "../components/parties/PartyFormModal";
import { CustomerDetailDrawer } from "../components/agreements/CustomerDetailDrawer";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

const KIND_LABELS: Record<PartyKind, { title: string; sub: string; cta: string; col1: string }> = {
  supplier: {
    title: "Tedarikçiler",
    sub: "Hammadde ve sarf tedarikçileri",
    cta: "Yeni Tedarikçi",
    col1: "Firma Adı",
  },
  customer: {
    title: "Müşteriler",
    sub: "Mamul sevkiyatı yapılan müşteriler",
    cta: "Yeni Müşteri",
    col1: "Ad",
  },
};

interface PartyRow {
  id: string;
  code: string;
  name: string;
  taxNumber: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  isActive: boolean;
}

export function PartiesPage() {
  const { kind: kindParam } = useParams<{ kind?: string }>();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const kind: PartyKind = kindParam === "customers" ? "customer" : "supplier";

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PartyRow | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<
    null | { title: string; message: ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }
  >(null);

  const input = useMemo(() => ({ page, pageSize, q: q.trim() || undefined }), [page, q]);

  const supplierQ = trpc.supplier.list.useQuery(input, { enabled: kind === "supplier" });
  const customerQ = trpc.customer.list.useQuery(input, { enabled: kind === "customer" });
  const activeQ = kind === "supplier" ? supplierQ : customerQ;

  const toggleSupplier = trpc.supplier.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const toggleCustomer = trpc.customer.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });

  // Süper admin: tedarikçi/müşteriyi çift onaylı kalıcı silme
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const hardDeleteCustomer = trpc.customer.hardDelete.useMutation();
  const hardDeleteSupplier = trpc.supplier.hardDelete.useMutation();
  const hardDeleteMut = kind === "supplier" ? hardDeleteSupplier : hardDeleteCustomer;
  const [delTarget, setDelTarget] = useState<PartyRow | null>(null);
  function runHardDelete() {
    if (!delTarget) return;
    hardDeleteMut.mutate({ id: delTarget.id }, {
      onSuccess: () => { setDelTarget(null); hardDeleteMut.reset(); qc.invalidateQueries(); },
    });
  }

  const labels = KIND_LABELS[kind];
  const rows = (activeQ.data?.rows ?? []) as unknown as PartyRow[];
  const total = activeQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(row: PartyRow) {
    setEditing(row);
    setModalOpen(true);
  }
  function handleToggle(row: PartyRow) {
    const newState = !row.isActive;
    const payload = { id: row.id, isActive: newState };
    const runToggle = () => {
      if (kind === "supplier") toggleSupplier.mutate(payload);
      else toggleCustomer.mutate(payload);
    };
    setConfirm(
      newState
        ? {
            title: "Kaydı aktifleştir?",
            message: <><b>{row.code}</b> kaydını tekrar aktifleştir?</>,
            tone: "primary",
            confirmLabel: "Aktifleştir",
            run: runToggle,
          }
        : {
            title: "Kaydı pasifleştir?",
            message: <><b>{row.code}</b> kaydını pasifleştir? (Soft delete)</>,
            tone: "danger",
            confirmLabel: "Pasifleştir",
            run: runToggle,
          }
    );
  }

  const drawers = (
    <>
      <PartyFormModal
        open={modalOpen}
        kind={kind}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />

      <CustomerDetailDrawer
        open={!!detailId}
        customerId={detailId}
        onClose={() => setDetailId(null)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />

      <HardDeleteConfirm
        open={!!delTarget}
        code={delTarget?.code ?? ""}
        title={kind === "supplier" ? "Tedarikçiyi kalıcı sil" : "Müşteriyi kalıcı sil"}
        warning={
          kind === "supplier" ? (
            <>
              <b>{delTarget?.code}</b> — {delTarget?.name} tedarikçisini <b>KALICI</b> olarak sileceksiniz.
              Bu tedarikçiye ait satınalma siparişi, mal kabul (lot), stok kartı veya QC sertifikası varsa silme engellenir.
            </>
          ) : (
            <>
              <b>{delTarget?.code}</b> — {delTarget?.name} müşterisini <b>KALICI</b> olarak sileceksiniz.
              Bu müşteriye ait sipariş veya fiyat anlaşması varsa silme engellenir.
            </>
          )
        }
        busy={hardDeleteMut.isPending}
        errorMessage={hardDeleteMut.error?.message ?? null}
        onConfirmed={runHardDelete}
        onClose={() => { setDelTarget(null); hardDeleteMut.reset(); }}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={openCreate}><Icon name="plus" size={15} />{labels.cta}</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="Kod, ad veya vergi no ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
        </div>

        {activeQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : activeQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{activeQ.error?.message}</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q ? `"${q}" için sonuç yok.` : "Henüz kayıt yok"}</div>
        ) : (
          <>
            {rows.map((r) => {
              const clickable = kind === "customer";
              return (
                <MCard pad key={r.id} onClick={clickable ? () => setDetailId(r.id) : undefined} style={clickable ? { cursor: "pointer" } : undefined}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.name}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                    </span>
                    {r.isActive ? <span className="badge b-success no-dot">Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                  </div>
                  {(r.taxNumber || r.city) ? (
                    <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)" }}>
                      {r.taxNumber ? <span><span style={{ color: "var(--neutral-400)" }}>VKN </span><span className="mono">{r.taxNumber}</span></span> : null}
                      {r.city ? <span>{r.city}{r.country ? ` · ${r.country}` : ""}</span> : null}
                    </div>
                  ) : null}
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    {kind === "customer" ? (
                      <button type="button" className="m-iconbtn m-li-act" aria-label="Fiyat anlaşmaları" onClick={() => setDetailId(r.id)}><Icon name="list2" size={16} /></button>
                    ) : null}
                    <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => openEdit(r)}><Icon name="edit" size={16} /></button>
                    <button type="button" className="m-iconbtn m-li-act" style={{ color: r.isActive ? "var(--danger)" : "var(--success)" }} aria-label={r.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => handleToggle(r)} disabled={toggleSupplier.isPending || toggleCustomer.isPending}><Icon name={r.isActive ? "archive" : "check"} size={16} /></button>
                    {isSuperAdmin ? (
                      <button type="button" className="m-iconbtn m-li-act danger" aria-label="Kalıcı sil" onClick={() => { hardDeleteMut.reset(); setDelTarget(r); }}><Icon name="trash" size={16} /></button>
                    ) : null}
                  </div>
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">{labels.title}</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">{labels.title}</h1>
          <p className="sub">{labels.sub}</p>
        </div>
        <button type="button" className="btn-md primary" onClick={openCreate}>
          <Icon name="plus" size={14} />{labels.cta}
        </button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Kod, ad veya vergi no ara…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              aria-label="Ara"
            />
          </div>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => activeQ.refetch()} disabled={activeQ.isFetching}>
            {activeQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        <div className="dt2-scroll">
          {activeQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : activeQ.isError ? (
            <div className="empty-state">
              <p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p>
              <p className="sub">{activeQ.error?.message}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Henüz kayıt yok</p>
              <p className="sub">{q ? `"${q}" için sonuç yok.` : "İlk kaydı ekleyin."}</p>
              {!q ? (
                <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={openCreate}>
                  <Icon name="plus" size={14} />{labels.cta}
                </button>
              ) : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Kod</th>
                  <th>{labels.col1}</th>
                  <th style={{ width: 130 }}>Vergi No</th>
                  <th style={{ width: 140 }}>Şehir / Ülke</th>
                  <th style={{ width: 160 }}>İletişim</th>
                  <th style={{ width: 90 }}>Durum</th>
                  <th style={{ width: kind === "customer" ? 150 : 116 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><span className="cell-id">{r.code}</span></td>
                    <td>
                      {r.name}
                      {r.address ? <span className="sec">{r.address}</span> : null}
                    </td>
                    <td>{r.taxNumber ? <span className="mono" style={{ fontSize: 12 }}>{r.taxNumber}</span> : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td>{r.city ? `${r.city}${r.country ? " · " + r.country : ""}` : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.email ? <div style={{ color: "var(--neutral-800)" }}>{r.email}</div> : null}
                      {r.phone ? <div style={{ color: "var(--neutral-500)" }}>{r.phone}</div> : null}
                      {!r.email && !r.phone ? <span style={{ color: "var(--neutral-400)" }}>—</span> : null}
                    </td>
                    <td>
                      {r.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}
                    </td>
                    <td>
                      <div className="row-acts">
                        {kind === "customer" ? (
                          <button
                            type="button"
                            className="iact"
                            aria-label="Fiyat anlaşmaları"
                            title="Fiyat anlaşmaları"
                            onClick={() => setDetailId(r.id)}
                          >
                            <Icon name="list2" size={15} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="iact"
                          aria-label="Düzenle"
                          title="Düzenle"
                          onClick={() => openEdit(r)}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                        <button
                          type="button"
                          className={r.isActive ? "iact danger" : "iact success"}
                          aria-label={r.isActive ? "Pasifleştir" : "Aktifleştir"}
                          title={r.isActive ? "Pasifleştir" : "Aktifleştir"}
                          onClick={() => handleToggle(r)}
                          disabled={toggleSupplier.isPending || toggleCustomer.isPending}
                        >
                          <Icon name={r.isActive ? "archive" : "check"} size={15} />
                        </button>
                        {isSuperAdmin ? (
                          <button
                            type="button"
                            className="iact danger"
                            aria-label="Kalıcı sil"
                            title="Kalıcı sil (süper admin)"
                            onClick={() => { hardDeleteMut.reset(); setDelTarget(r); }}
                          >
                            <Icon name="trash" size={15} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div>
            <b>{total}</b> kayıttan <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası
          </div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}
